/**
 * Staff (Settings → Staff; help `cloud.staff`): the organisation's staff register (decision 422). Every staff member
 * reads it; tenant admins, managers and office staff add people, change their roles, title and contact details, link
 * them to a Teslyn sign-in and make them inactive when they leave. Tenant admins and managers also delete a person
 * (MB, 25 Oct 2026; decision 601), after a dialog that names them, says what happens and offers "Mark as left
 * (inactive)" instead: a soft delete, so old records keep the name, restorable from "Deleted people" for 30 days, then
 * removed for good with the names kept as text (decision 602). Pilots and operators fill every pilot and
 * operator drop-down in Teslyn, offline in Teslyn Field too.
 */
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  REGISTER_ROLES,
  REGISTER_ROLE_LABEL,
  canDeleteStaff,
  canEditStaffRegister,
  deletedNameOffer,
  findDeletedPersonByName,
  isActivePerson,
  legacyPersonRole,
  lifecycleDateText,
  personRemovalText,
  PERSON_BIN_DAYS,
  normalisePersonName,
  normaliseRoles,
  registerRolesLabel,
  validatePerson,
  type Person,
  type RegisterRole,
  type ValidationIssue,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { CheckField, IssueList, SelectField, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader, SectionHeader } from '../components/States.tsx';
import {
  checkAddedOnSite,
  loadAddedOnSite,
  deleteStaff,
  loadSignIns,
  loadStaff,
  restoreStaff,
  saveStaff,
  type SignInChoice,
} from './api.ts';
import { StaffImportPanel } from './StaffImportPanel.tsx';
// Invite to Teslyn (decision 660)
import {
  canInviteToTeslyn,
  hasPendingInvite,
  inviteLinkMessage,
  staffInviteNote,
  type StaffInvite,
} from '@teslyn/domain';
import { inviteToTeslyn, loadStaffInvites } from './invite.ts';
import { InviteToTeslynPanel } from './InviteToTeslyn.tsx';
import { TableWrap } from '../components/TableWrap.tsx';

export interface StaffScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  /** The viewer's tenant roles (display only; RLS decides). */
  readonly roles: readonly string[];
  /** Where invitation links land (this portal's address), for "Invite to Teslyn". */
  readonly redirectTo?: string;
  /** Shown as a tab of a page (People, Company, Projects' Deleted list): no page title of its own. */
  readonly embedded?: boolean;
}

interface Draft {
  readonly name: string;
  readonly title: string;
  readonly roles: readonly RegisterRole[];
  readonly email: string;
  readonly phone: string;
  readonly userId: string;
}

const BLANK: Draft = { name: '', title: '', roles: [], email: '', phone: '', userId: '' };

const draftOf = (p: Person): Draft => ({
  name: p.name,
  title: p.title ?? '',
  roles: normaliseRoles(p.roles ?? [p.role]),
  email: p.email ?? '',
  phone: p.phone ?? '',
  userId: p.user_id ?? '',
});

export function StaffScreen({
  sb,
  tenantId,
  roles,
  redirectTo,
  embedded = false,
}: StaffScreenProps): ReactElement {
  // inside a page with tabs (the menu consolidation): the tab's own header, under the page's title
  const Head = embedded ? SectionHeader : PageHeader;
  const editable = canEditStaffRegister(roles);
  const deletes = canDeleteStaff(roles);
  const invites = canInviteToTeslyn(roles);
  // the organisation's staff invitations, for each row's "Invited <date>" and the like; who is being invited now
  const [sent, setSent] = useState<readonly StaffInvite[]>([]);
  // N11 (decision 471): the people field crew added on site, for the office to check
  const [onSite, setOnSite] = useState<ReadonlySet<string>>(new Set());
  const [invitee, setInvitee] = useState<Person | null>(null);
  // everyone in the register, deleted people too (only tenant admins and managers see those, under "Deleted people")
  const [people, setPeople] = useState<Person[] | null>(null);
  const [signIns, setSignIns] = useState<readonly SignInChoice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Person | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft>(BLANK);
  const [issues, setIssues] = useState<readonly ValidationIssue[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  // "Delete …" pressed: the dialog that names the person and says what happens
  const [confirm, setConfirm] = useState<Person | null>(null);
  // "Add person" with a deleted person's name (decision 663): the offer to restore them instead
  const [offer, setOffer] = useState<Person | null>(null);

  const load = useCallback(() => {
    setError(null);
    setPeople(null);
    loadStaff(sb, tenantId, 'all')
      .then(setPeople)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
    void loadAddedOnSite(sb, tenantId).then(setOnSite);
    if (editable) {
      loadSignIns(sb, tenantId)
        .then(setSignIns)
        .catch(() => setSignIns([]));
      void loadStaffInvites(sb, tenantId).then(setSent);
    }
  }, [sb, tenantId, editable]);
  useEffect(load, [load]);

  const replace = (saved: Person): void =>
    setPeople((list) => [...(list ?? []).filter((p) => p.id !== saved.id), saved]);

  async function submit(addAnyway = false): Promise<void> {
    if (!people || !editing) return;
    const roles = normaliseRoles(draft.roles);
    const base: Person =
      editing === 'new'
        ? {
            id: '',
            created_at: '',
            updated_at: '',
            updated_by: null,
            deleted_at: null,
            tenant_id: tenantId,
            name: '',
            role: 'manager',
            rank: null,
            active: true,
          }
        : editing;
    const candidate: Person = {
      ...base,
      name: normalisePersonName(draft.name),
      title: draft.title.trim(),
      roles,
      role: legacyPersonRole(roles),
      email: draft.email.trim() || null,
      phone: draft.phone.trim() || null,
      user_id: draft.userId || null,
    };
    const found = validatePerson(candidate, people);
    setIssues(found);
    if (found.length > 0) return;
    // decision 663: a new person with a deleted person's name: offer to restore them instead
    const gone =
      editing === 'new' && !addAnyway ? findDeletedPersonByName(people, candidate.name) : undefined;
    setOffer(gone ?? null);
    if (gone) return;
    setBusy(true);
    try {
      const saved = await saveStaff(sb, candidate, editing === 'new');
      replace(saved);
      setMessage(editing === 'new' ? `Added ${saved.name}.` : `Saved ${saved.name}.`);
      setEditing(null);
      setDraft(BLANK);
    } catch (e) {
      setIssues([{ field: 'save', message: e instanceof Error ? e.message : String(e) }]);
    } finally {
      setBusy(false);
    }
  }

  async function checked(p: Person): Promise<void> {
    try {
      await checkAddedOnSite(sb, p.id);
      setOnSite((s) => new Set([...s].filter((x) => x !== p.id)));
      setMessage(`${p.name} is checked: no longer marked as added on site.`);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : String(e));
    }
  }

  async function setActive(p: Person, active: boolean): Promise<void> {
    try {
      replace(await saveStaff(sb, { ...p, active }, false));
      setMessage(active ? `${p.name} is active again.` : `${p.name} is now inactive.`);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : String(e));
    }
  }

  async function remove(p: Person): Promise<void> {
    setConfirm(null);
    try {
      const at = await deleteStaff(sb, p.id);
      replace({ ...p, deleted_at: at });
      setMessage(
        `${p.name} is deleted from the staff register. Records that name them keep the name; restore them under Deleted people.`,
      );
    } catch (e) {
      setMessage(e instanceof Error ? e.message : String(e));
    }
  }

  async function restore(p: Person): Promise<boolean> {
    try {
      await restoreStaff(sb, p.id);
      replace({ ...p, deleted_at: null });
      setMessage(`${p.name} is back in the staff register.`);
      return true;
    } catch (e) {
      setMessage(e instanceof Error ? e.message : String(e));
      return false;
    }
  }

  /** "Restore …" in the offer of "Add person": the deleted person comes back and the form closes. */
  async function restoreInstead(p: Person): Promise<void> {
    setBusy(true);
    try {
      if (await restore(p)) {
        setOffer(null);
        setEditing(null);
        setDraft(BLANK);
      }
    } finally {
      setBusy(false);
    }
  }

  const live = (people ?? []).filter((p) => p.deleted_at === null);
  const deleted = (people ?? [])
    .filter((p) => p.deleted_at !== null)
    .sort((a, b) => a.name.localeCompare(b.name, 'en-AU'));
  const sorted = [...live].sort(
    (a, b) =>
      Number(!isActivePerson(a)) - Number(!isActivePerson(b)) || a.name.localeCompare(b.name, 'en-AU'),
  );
  const signInName = (id: string | null | undefined): string =>
    id ? (signIns.find((s) => s.userId === id)?.label ?? 'Linked') : '';
  // a sign-in's name for the invitation notes: its display name, else its email
  const signInShort = (id: string): string | null =>
    signIns.find((s) => s.userId === id)?.label.replace(/\s*\([^()]*\)$/, '') ?? null;
  const linkedUsers = new Set(live.flatMap((p) => (p.user_id ? [p.user_id] : [])));

  async function sendInvite(
    p: Person,
    email: string,
    inviteRoles: Parameters<typeof inviteToTeslyn>[1]['roles'],
  ) {
    const { person, result } = await inviteToTeslyn(sb, {
      tenantId,
      person: p,
      email,
      roles: inviteRoles,
      redirectTo: redirectTo ?? window.location.origin,
    });
    replace(person);
    setInvitee(null);
    setMessage(inviteLinkMessage(email, result.status, result.link, p.name));
    load();
  }
  const formName = editing === 'new' ? 'Add person' : editing ? `Edit ${editing.name}` : '';

  return (
    <section className={embedded ? 'stack' : 'page'}>
      <Head
        title="Staff register"
        subtitle="The staff register: everyone in the organisation, with their roles. Pilots and operators fill every pilot and operator list in Teslyn."
      >
        {editable && !editing && !importing && (
          <>
            <Button
              variant="primary"
              onClick={() => {
                setEditing('new');
                setDraft(BLANK);
                setIssues([]);
                setOffer(null);
              }}
            >
              Add person
            </Button>
            <Button
              onClick={() => {
                setImporting(true);
                setMessage(null);
              }}
            >
              Import staff
            </Button>
          </>
        )}
      </Head>
      {importing && people && (
        <StaffImportPanel
          sb={sb}
          tenantId={tenantId}
          people={people}
          onCancel={() => setImporting(false)}
          onDone={(m) => {
            setImporting(false);
            setMessage(m);
            load();
          }}
        />
      )}
      {!editable && (
        <p className="body muted">
          Tenant admins, managers and office staff change the staff register. Ask one of them to add someone
          or change their roles.
        </p>
      )}
      {message && (
        <p className="body" role="status">
          {message}
        </p>
      )}
      {editing && (
        <form
          className="panel stack"
          aria-label={formName}
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <h2 className="heading">{formName}</h2>
          <IssueList issues={issues} title="Not saved" />
          <div className="form-grid">
            <TextField
              label="Name"
              value={draft.name}
              onChange={(v) => {
                setDraft({ ...draft, name: v });
                setOffer(null);
              }}
            />
            <TextField
              label="Title"
              hint="Free text, for example CEO or Chief pilot."
              value={draft.title}
              onChange={(v) => setDraft({ ...draft, title: v })}
            />
            <TextField
              label="Email (optional)"
              type="email"
              value={draft.email}
              onChange={(v) => setDraft({ ...draft, email: v })}
            />
            <TextField
              label="Phone (optional)"
              type="tel"
              value={draft.phone}
              onChange={(v) => setDraft({ ...draft, phone: v })}
            />
            <SelectField
              label="Teslyn sign-in"
              hint="Optional: the account this person signs in with. Many register people have none."
              value={draft.userId}
              onChange={(v) => setDraft({ ...draft, userId: v })}
              options={[['', 'No sign-in'], ...signIns.map((s) => [s.userId, s.label] as const)]}
            />
          </div>
          <fieldset className="fieldset form-grid">
            <legend className="label muted">Roles</legend>
            {REGISTER_ROLES.map((r) => (
              <CheckField
                key={r}
                label={REGISTER_ROLE_LABEL[r]}
                checked={draft.roles.includes(r)}
                onChange={(on) =>
                  setDraft({ ...draft, roles: on ? [...draft.roles, r] : draft.roles.filter((x) => x !== r) })
                }
              />
            ))}
          </fieldset>
          {editing === 'new' && offer ? (
            <div className="notice stack" role="group" aria-label={`${offer.name} was deleted`}>
              <p className="body">{deletedNameOffer(offer, deletes)}</p>
              <div className="row">
                {deletes && (
                  <Button variant="primary" busy={busy} onClick={() => void restoreInstead(offer)}>
                    {`Restore ${offer.name}`}
                  </Button>
                )}
                <Button onClick={() => void submit(true)}>Add a new person</Button>
                <Button
                  variant="quiet"
                  onClick={() => {
                    setOffer(null);
                    setEditing(null);
                    setIssues([]);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="row">
              <Button type="submit" variant="primary" busy={busy}>
                Save
              </Button>
              <Button
                variant="quiet"
                onClick={() => {
                  setEditing(null);
                  setIssues([]);
                }}
              >
                Cancel
              </Button>
            </div>
          )}
        </form>
      )}
      {invitee && (
        <InviteToTeslynPanel
          person={invitee}
          onSend={(email, r) => sendInvite(invitee, email, r)}
          onCancel={() => setInvitee(null)}
        />
      )}
      {error && <ErrorState message={error} onRetry={load} />}
      {!people && !error && <Loading what="the staff register" />}
      {confirm && (
        <DeleteStaffDialog
          person={confirm}
          onDelete={() => void remove(confirm)}
          onMarkLeft={() => {
            setConfirm(null);
            void setActive(confirm, false);
          }}
          onCancel={() => setConfirm(null)}
        />
      )}
      {people && live.length === 0 && (
        <div className="panel">
          <p className="body">
            No one in the staff register yet. A project file imported in Teslyn Field adds its crew; or add
            people here, or press &quot;Import staff&quot;.
          </p>
        </div>
      )}
      {people && live.length > 0 && (
        <TableWrap>
          <table className="table" aria-label="Staff register">
            <thead>
              <tr>
                <th className="label">Name</th>
                <th className="label">Roles</th>
                <th className="label">Title</th>
                <th className="label">Contact</th>
                <th className="label">Sign-in</th>
                <th className="label">Status</th>
                {editable && (
                  <th className="label">
                    <span className="sr-only">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {sorted.map((p) => {
                const active = isActivePerson(p);
                const note = editable ? staffInviteNote(p, sent, signInShort, linkedUsers) : null;
                return (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td>{registerRolesLabel(p)}</td>
                    <td>{p.title ?? ''}</td>
                    <td>{[p.email, p.phone].filter(Boolean).join(' · ')}</td>
                    <td>
                      {signInName(p.user_id)}
                      {note && (
                        <div className={note.kind === 'invited' ? 'caption' : 'caption muted'}>
                          {note.text}
                        </div>
                      )}
                    </td>
                    <td>
                      {active ? 'Active' : 'Inactive'}
                      {onSite.has(p.id) && (
                        <div className="caption">
                          Added on site in Teslyn Field: check their name and roles
                        </div>
                      )}
                    </td>
                    {editable && (
                      <td className="table__actions">
                        {onSite.has(p.id) && (
                          <Button
                            variant="quiet"
                            aria-label={`${p.name} checked`}
                            onClick={() => void checked(p)}
                          >
                            Checked
                          </Button>
                        )}
                        <Button
                          variant="quiet"
                          aria-label={`Edit ${p.name}`}
                          onClick={() => {
                            setEditing(p);
                            setDraft(draftOf(p));
                            setIssues([]);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="quiet"
                          aria-label={active ? `Make ${p.name} inactive` : `Make ${p.name} active`}
                          onClick={() => void setActive(p, !active)}
                        >
                          {active ? 'Make inactive' : 'Make active'}
                        </Button>
                        {invites && !p.user_id && (
                          <Button
                            variant="quiet"
                            aria-label={
                              hasPendingInvite(p.id, sent)
                                ? `Invite ${p.name} to Teslyn again`
                                : `Invite ${p.name} to Teslyn`
                            }
                            onClick={() => {
                              setMessage(null);
                              setEditing(null);
                              setInvitee(p);
                            }}
                          >
                            {hasPendingInvite(p.id, sent) ? 'Invite again' : 'Invite to Teslyn'}
                          </Button>
                        )}
                        {deletes && (
                          <Button
                            variant="quiet"
                            aria-label={`Delete ${p.name}`}
                            onClick={() => {
                              setMessage(null);
                              setConfirm(p);
                            }}
                          >
                            Delete…
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      )}
      {deletes && deleted.length > 0 && (
        <section className="stack" aria-labelledby="deleted-people-title">
          <h2 id="deleted-people-title" className="heading">
            Deleted people
          </h2>
          <p className="body muted">
            Deleted by a tenant admin or manager. They are in no list for new records; the records that name
            them keep the name. Restore puts them back in the register as they were. After {PERSON_BIN_DAYS}{' '}
            days they are removed for good and can no longer be restored; the records keep the name as text.
          </p>
          <TableWrap>
            <table className="table" aria-label="Deleted people">
              <thead>
                <tr>
                  <th className="label">Name</th>
                  <th className="label">Roles</th>
                  <th className="label">Deleted</th>
                  <th className="label">Removed for good</th>
                  <th className="label">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {deleted.map((p) => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td>{registerRolesLabel(p)}</td>
                    <td>{lifecycleDateText(p.deleted_at!)}</td>
                    <td>
                      {(() => {
                        const r = personRemovalText(p.deleted_at!);
                        return `On ${r.date}, ${r.inWords}`;
                      })()}
                    </td>
                    <td className="table__actions">
                      <Button
                        variant="quiet"
                        aria-label={`Restore ${p.name}`}
                        onClick={() => void restore(p)}
                      >
                        Restore
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </section>
      )}
    </section>
  );
}

/**
 * Delete a person (tenant admins and managers): names them, says what happens, and offers "Mark as left (inactive)", the gentler
 * choice for someone who has left, while they are still active. Escape or Cancel closes it with nothing changed.
 */
function DeleteStaffDialog({
  person,
  onDelete,
  onMarkLeft,
  onCancel,
}: {
  readonly person: Person;
  readonly onDelete: () => void;
  readonly onMarkLeft: () => void;
  readonly onCancel: () => void;
}): ReactElement {
  // focus on Cancel, the safe choice, so Enter does not delete
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => box.current?.querySelector<HTMLButtonElement>('[data-cancel]')?.focus(), []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);
  const active = isActivePerson(person);
  return (
    <div ref={box} className="panel confirm stack" role="dialog" aria-labelledby="delete-person-title">
      <h2 id="delete-person-title" className="heading">
        Delete {person.name} from the staff register?
      </h2>
      <p className="body">
        {person.name} leaves the staff register, every pilot and operator list, the project crews and the
        Operations Hub, here and on Teslyn Field laptops when they next sync. The flights, daily entries, crew
        lists and reports that name them keep the name {person.name}. A tenant admin or manager can restore
        them under Deleted people for {PERSON_BIN_DAYS} days, until{' '}
        {personRemovalText(new Date().toISOString()).date}; after that they are removed for good, and the
        records keep the name as text.
      </p>
      {person.user_id && (
        <p className="body">
          Their Teslyn sign-in is not removed: take away their access in Users &amp; roles.
        </p>
      )}
      {active && (
        <p className="body muted">
          If {person.name} has left, you can mark them as left instead: they stay in the register as inactive,
          out of the lists for new records.
        </p>
      )}
      <div className="row">
        <Button className="btn--danger" onClick={onDelete}>
          Delete {person.name}
        </Button>
        {active && <Button onClick={onMarkLeft}>Mark as left (inactive)</Button>}
        <Button variant="quiet" data-cancel="" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
